import { createEffect, createMemo, For, Show } from "solid-js";
import clsx from "~/util/html/style";
import type { AppointmentTemplate, FormData } from "./types";
import { StepActions } from "./_step_actions";
import { useI18n } from "./i18n";

export function AppointmentInfoStep(props: {
  templates: AppointmentTemplate[];
  formData: FormData;
  onUpdate: (partial: Partial<FormData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const t = useI18n();

  const selectedTemplate = createMemo(
    () => props.templates.find((tp) => tp.id === props.formData.appointmentTemplateId) ?? null,
  );

  const requireNotes = () => selectedTemplate()?.requireNotes ?? false;

  const canContinue = () => {
    if (!props.formData.appointmentTemplateId) return false;
    if (requireNotes() && !props.formData.chiefComplaint.trim()) return false;
    return true;
  };

  const handleTemplateSelect = (id: string) => {
    props.onUpdate({ appointmentTemplateId: id });
  };

  // Auto-select if only one template (but don't auto-advance — user still sees the notes field)
  createEffect(() => {
    if (props.templates.length === 1 && !props.formData.appointmentTemplateId) {
      props.onUpdate({ appointmentTemplateId: props.templates[0]!.id });
    }
  });

  return (
    <div class="flex flex-col gap-5">
      <h2 class="text-xl font-semibold text-typography-primary">{t("what_type")}</h2>

      <Show
        when={props.templates.length > 0}
        fallback={
          <div class="rounded-xl bg-amber-50 px-4 py-6 text-center">
            <p class="text-sm font-medium text-amber-800">{t("no_types_available")}</p>
          </div>
        }
      >
        <div class="flex flex-col gap-2.5">
          <For each={props.templates}>
            {(tmpl) => {
              const selected = () => props.formData.appointmentTemplateId === tmpl.id;
              return (
                <button
                  type="button"
                  class={clsx(
                    "flex items-start justify-between gap-3 rounded-2xl border p-4 text-left transition-all active:scale-[0.98]",
                    selected()
                      ? "border-brand-primary bg-brand-light ring-1 ring-brand-primary"
                      : "border-brand-subtle hover:border-brand-primary/50 hover:bg-brand-light/50",
                  )}
                  onClick={() => handleTemplateSelect(tmpl.id)}
                >
                  <div class="flex flex-col gap-1">
                    <p class="font-medium text-typography-primary">{tmpl.name}</p>
                    {/* The practice's own words, so it is rendered verbatim rather
                        than through i18n, and line breaks they typed are kept. */}
                    <Show when={tmpl.bookingInstructions?.trim()}>
                      {(instructions) => (
                        <p class="text-sm whitespace-pre-line text-typography-secondary">
                          {instructions()}
                        </p>
                      )}
                    </Show>
                  </div>
                  <div
                    class={clsx(
                      "flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                      selected() ? "border-brand-primary bg-brand-primary" : "border-brand-subtle",
                    )}
                  >
                    <Show when={selected()}>
                      <svg
                        class="size-3.5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="3"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </Show>
                  </div>
                </button>
              );
            }}
          </For>
        </div>

        <Show when={props.formData.appointmentTemplateId}>
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-typography-primary">
              {t("additional_info")}
              <Show when={requireNotes()}>
                <span class="ml-1 text-red-500">*</span>
              </Show>
            </label>
            <textarea
              class="min-h-[80px] rounded-lg border border-brand-subtle px-3 py-2 text-sm text-typography-primary transition-colors outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-focus"
              rows={3}
              value={props.formData.chiefComplaint}
              onInput={(e) => props.onUpdate({ chiefComplaint: e.currentTarget.value })}
              placeholder={t("describe_concern")}
            />
          </div>
        </Show>
      </Show>

      <StepActions onBack={props.onBack} onNext={props.onNext} nextDisabled={!canContinue()} />
    </div>
  );
}
